import React, { useEffect, useState } from "react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import SmartDataTable from "../../components/shared/SmartDataTable";
import { Search, Bell, Trophy, Send, Loader2 } from "lucide-react";
import { winnersApi } from "../../services/api";

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";
}

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

const LIMIT = 25;

export default function AdminWinners() {
  const [winners, setWinners] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page] = useState(1);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");

  async function load(p = 1) {
    setLoading(true);
    setError("");
    try {
      const res = await winnersApi.getAll({ limit: 1000, page: p });
      setWinners(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load winners.");
      setWinners([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(page); }, [page]);

  async function handleReleasePayout(id) {
    setActionId(id);
    setError("");
    try {
      const { data } = await winnersApi.markPaid(id);
      setWinners((prev) => prev.map((winner) => (
        winner._id === id ? { ...winner, ...data.data } : winner
      )));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to release payout.");
      setWinners((prev) => prev.map((winner) => (
        winner._id === id ? { ...winner, payoutStatus: "failed" } : winner
      )));
    } finally {
      setActionId(null);
    }
  }

  const columns = [
    { name: "Draw", selector: (row) => row.result?.drawDate || row.createdAt || "", sortable: true, cell: (row) => fmtDate(row.result?.drawDate || row.createdAt) },
    { name: "Pool", selector: (row) => row.pool?.poolId || "", sortable: true, cell: (row) => <span className="table-code">{row.pool?.poolId || "-"}</span> },
    { name: "User", selector: (row) => row.user?.name || "", sortable: true, cell: (row) => <strong>{row.user?.name || "-"}</strong>, grow: 1.4 },
    { name: "Ticket", selector: (row) => row.ticket?.ticketNumber || "", sortable: true, cell: (row) => <span className="table-code">{row.ticket?.ticketNumber || "-"}</span> },
    { name: "Share", selector: (row) => row.individualShare || 0, sortable: true, cell: (row) => <b className="table-primary">{fmt(row.individualShare)}</b> },
    { name: "Account", selector: (row) => row.user?.payoutOnboardingStatus || "", sortable: true, cell: (row) => <span className={`table-status ${row.user?.payoutOnboardingStatus === "active" ? "live" : "warn"}`}>{row.user?.payoutOnboardingStatus?.replaceAll("_", " ") || "not set"}</span> },
    { name: "Payout", selector: (row) => row.payoutStatus || "", sortable: true, cell: (row) => <span className={`table-status ${row.payoutStatus === "paid" ? "live" : row.payoutStatus === "failed" ? "danger" : "warn"}`}>{row.payoutStatus}</span> },
    {
      name: "Actions",
      button: true,
      cell: (row) => row.payoutStatus !== "paid" ? (
        <button onClick={() => handleReleasePayout(row._id)} disabled={actionId === row._id || row.user?.payoutOnboardingStatus !== "active"} title={row.user?.payoutOnboardingStatus === "active" ? "Release payout" : "Winner must complete payout setup"} style={{ color: "var(--green)" }}>
          {actionId === row._id ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
        </button>
      ) : null,
    },
  ];

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-title">Winners</div>
          <label className="admin-search"><Search size={15} /><input placeholder="Search winners..." /></label>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>

        <div className="admin-content">
          {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}
          <section className="admin-panel" style={{ padding: 0 }}>
            <div className="panel-heading">
              <h2><Trophy size={18} /> Winner Records
                <span className="muted-count" style={{ marginLeft: 8 }}>{total} total</span>
              </h2>
            </div>
            <SmartDataTable
              columns={columns}
              data={winners}
              loading={loading}
              searchFields={["pool.poolId", "user.name", "user.email", "ticket.ticketNumber", "payoutStatus"]}
              searchPlaceholder="Search winners by user, pool, ticket, payout..."
              emptyMessage="No winners calculated yet. Run the checker after entering results."
              paginationPerPage={LIMIT}
            />
          </section>
        </div>
      </main>
    </div>
  );
}
